import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import {
    IAttributeConfData,
    IAttributeConfDataOption,
    ProductConfigurableOptionsComponentDef
} from '@df/catalog-ui/product/configurable-options/product-configurable-options.component.def';
import { Product } from '@df/catalog/product/product';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { first } from 'rxjs/operators';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { GiftcardsProductViewComponent, personaliseOccasion } from './giftcards-product-view.component';
import { NGiftcardsPersonaliseModal } from './personalise/modal/giftcards-personalise-modal.component';
@Component({
    selector: 'giftcards-product-configurable-options',
    templateUrl: './giftcards-product-configurable-options.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./giftcards-product-configurable-options.scss'],
    standalone: true,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    imports: [
        TranslationModule,
        DfUiModule,
        ImageModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        ShellComponent,
        FormsModule,
        CarouselModule
    ]
})
export class GiftcardsProductConfigurableOptionsComponent extends ProductConfigurableOptionsComponentDef {
    readonly personaliseModalId = NGiftcardsPersonaliseModal.MODAL_ID;
    protected personaliseOccasionAttrOption: IAttributeConfDataOption | undefined;
    private dfModalService = inject(DfModalService);
    private giftcardsProductViewComponent = inject(GiftcardsProductViewComponent);
    protected displayCustomFieldInputError = false;

    @Input({ required: true })
    request!: Giftcards.ISelectionContainer;

    @Input()
    wrapElements = false;

    @Input()
    form?: NgForm;

    get sizeId(): number {
        return this.app.client.sizeId;
    }

    get havePersonaliseOccasion(): boolean {
        return !!this.personaliseOccasionAttrOption;
    }

    get personaliseProduct(): Product | undefined {
        return this.personaliseOccasionAttrOption?.product;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.giftcardsProductViewComponent?.openPersonaliseEmmiter.subscribe(() => this.openPersonaliseModal()));
    }

    ngAfterViewInit(): void {
        this.subscriptions.add(
            this.form?.ngSubmit.subscribe(() => {
                this.displayCustomFieldInputError =
                    !!this.form?.['submitAttempt'] && !this.request.file && this.isPersonaliseOccasionSelected;
                this.detectChanges();
            })
        );
    }

    protected get isPersonaliseOccasionSelected(): boolean {
        return this.giftcardsProductViewComponent.isPersonaliseOccasionSelected;
    }

    protected override setAttributes(attributes: IAttributeConfData[] | undefined): void {
        super.setAttributes(attributes);
        this.personaliseOccasionAttrOption = attributes
            ?.find(a => a.code === 'giftcard_occasion')
            ?.options?.find(option => option.label === personaliseOccasion);
        this.detectChanges();
    }

    override pick(code: string, option: IAttributeConfDataOption): void {
        this.displayCustomFieldInputError = false;
        super.pick(code, option);
        if (option.label === personaliseOccasion) {
            this.openPersonaliseModal();
        } else if (this.request.file) {
            this.giftcardsProductViewComponent?.updateCustomImage(true);
        }
    }

    private openPersonaliseModal(): void {
        if (this.personaliseProduct) {
            this.dfModalService.open(
                undefined,
                this.personaliseModalId,
                undefined,
                {
                    previewImage: this.personaliseProduct.data?.image,
                    request: this.request
                },
                modalRef => {
                    // After close trigger updateimage original view
                    modalRef
                        .afterClosed()
                        .pipe(first())
                        .subscribe(() => {
                            this.giftcardsProductViewComponent?.updateCustomImage();
                            this.detectChanges();
                        });
                }
            );
        }
    }
}
